<template>
  <div id="snacksModal" ref="modalRef" tabindex="-1" aria-hidden="true" class="modal hidden">
    <div v-if="isVisible" class="modal-body w-full sm:w-[950px] px-0! pt-0! pb-0! no-scrollbar">
      <CloseButton @close="cancel()"></CloseButton>
      <div class="modal-body__content">
        <h3 class="text-left font-bold text-2xl mb-2 flex items-center justify-between">
          <template v-if="selectedSnacks">Select Snacks </template>
          <template v-else>Supported Snacks</template>
        </h3>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5 text-left mb-8">
          <template v-for="(snack, index) in allSnacks" :key="snack">
            <h4
              class="w-full font-semibold mt-6 col-span-full flex items-center gap-2 text-gray-900"
              v-if="snack.type !== allSnacks[index - 1]?.type"
            >
              {{ snack.type }}
              <span class="bg-gray-100 text-gray-500 rounded-full px-1.5 text-[0.65rem]">{{
                numberOfSnacksPerType(snack.type)
              }}</span>
            </h4>

            <div
              class="card transition-animation"
              v-on:click="toggleSnack(snack)"
              :class="{ 'card--clickable': selectedSnacks, 'card--active': isSnackSelected(snack) }"
            >
              <svg
                v-if="selectedSnacks"
                class="hidden h-4 w-4"
                :class="{ 'card__active-icon': isSnackSelected(snack) }"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"
                />
              </svg>

              <h5 class="font-medium text-sm">{{ snack.emoji }} {{ snack.name }}</h5>
              <div class="flex flex-wrap items-center gap-1 text-xs text-gray-500">
                <span> {{ snack.calories }} kcal</span>
                •
                <span> {{ snack.amount }} </span>
              </div>
            </div>
          </template>
        </div>
      </div>
      <button
        v-if="selectedSnacks && selectedSnacks.length > 0"
        @click="closeModal()"
        class="shadow-2xl transition-animation sticky bg-primary-500 hover:bg-primary-600 rounded-xl text-white px-8 py-5 bottom-6 right-6 mx-auto font-semibold flex items-center justify-center gap-2"
      >
        <span>Save {{ selectedSnacks.length }} snacks</span>
        <svg
          class="w-6 h-6"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m5 12 4.7 4.5 9.3-9"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import SupportedSnacksModalHelperService from '@/services/supported-snacks-modal-helper'
import { snacksSortedBySnackTypeAndCalories } from '@shared/constants/snacks'
import type { Snack } from '@shared/interfaces/snack'
import { onMounted, ref } from 'vue'
import CloseButton from '../CloseButton.vue'

const allSnacks = snacksSortedBySnackTypeAndCalories()
const selectedSnacks = ref<Snack[] | undefined>()
const modalRef = ref<HTMLDivElement>()
const isVisible = ref<boolean>(false)

onMounted(() => {
  SupportedSnacksModalHelperService.init(modalRef, selectedSnacks, isVisible)
})

function closeModal(): void {
  SupportedSnacksModalHelperService.hide(selectedSnacks.value)
}

function cancel(): void {
  SupportedSnacksModalHelperService.hide()
}

function isSnackSelected(snack: Snack): boolean {
  return selectedSnacks.value?.some((selectedSnack) => selectedSnack.name === snack.name) ?? false
}

function toggleSnack(snack: Snack): void {
  if (!selectedSnacks.value) {
    return
  }

  const index = selectedSnacks.value.findIndex((selectedSnack) => selectedSnack.name === snack.name)

  if (index === -1) {
    selectedSnacks?.value?.push(snack)
    return
  }
  selectedSnacks?.value?.splice(index, 1)
}

function numberOfSnacksPerType(type: string): number {
  return allSnacks.filter((snack) => snack.type === type).length
}
</script>

<style scoped>
/* .footer::before {
  @apply absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-95;
  content: '';
} */
</style>
